import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { Insight } from "@stratify/shared";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { trackEvent } from "../../analytics/store/analyticsSlice";
import { deleteInsightStart, updateInsightStart } from "../store/insightsSlice";

interface InsightItemProps {
  insight: Insight;
}

export const InsightItem: React.FC<InsightItemProps> = ({ insight }) => {
  const dispatch = useDispatch();
  const [copyTooltip, setCopyTooltip] = useState("Copy to clipboard");

  const handleCopyClick = async () => {
    try {
      dispatch(
        trackEvent({
          name: "insight_copied",
          params: {
            document_id: insight.documentId,
            insight_id: insight.id,
          },
        })
      );
      const textToCopy = `${insight.description}`;
      await navigator.clipboard.writeText(textToCopy);
      setCopyTooltip("Copied!");
      setTimeout(() => {
        setCopyTooltip("Copy to clipboard");
      }, 2000);
    } catch (err) {
      alert("Failed to copy insight to clipboard.");
      console.error("Failed to copy text: ", err);
    }
  };

  const handleApproveClick = () => {
    dispatch(
      trackEvent({
        name: "insight_approved",
        params: {
          document_id: insight.documentId,
          insight_id: insight.id,
        },
      })
    );
    dispatch(
      updateInsightStart({
        documentId: insight.documentId,
        insightId: insight.id!,
        updates: { approved: true },
      })
    );
  };

  const handleDisapproveClick = () => {
    dispatch(
      trackEvent({
        name: "insight_disapproved",
        params: {
          document_id: insight.documentId,
          insight_id: insight.id,
        },
      })
    );
    dispatch(
      updateInsightStart({
        documentId: insight.documentId,
        insightId: insight.id!,
        updates: { approved: false },
      })
    );
  };

  const handleDeleteClick = () => {
    if (window.confirm("Are you sure you want to delete this insight?")) {
      dispatch(
        trackEvent({
          name: "insight_deleted",
          params: {
            document_id: insight.documentId,
            insight_id: insight.id,
          },
        })
      );
      dispatch(
        deleteInsightStart({
          documentId: insight.documentId,
          insightId: insight.id!,
        })
      );
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: "background.paper",
        borderRadius: 1,
        position: "relative",
      }}
      data-testid={`insight-${insight.id}`}
    >
      <Typography data-testid={`insight-${insight.id}-description`}>
        {insight.description}
      </Typography>
      <Box
        sx={{
          p: 2,
          top: 8,
          right: 8,
          display: "flex",
          gap: 1,
          justifyContent: "flex-end",
        }}
      >
        <Tooltip title="Approve insight">
          <IconButton
            onClick={handleApproveClick}
            color={insight.approved ? "primary" : "default"}
            size="small"
          >
            <ThumbUpIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Disapprove insight">
          <IconButton
            onClick={handleDisapproveClick}
            color={insight.approved === false ? "error" : "default"}
            size="small"
          >
            <ThumbDownIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete insight">
          <IconButton onClick={handleDeleteClick} color="error" size="small">
            <DeleteOutlineIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={copyTooltip}>
          <IconButton onClick={handleCopyClick} size="small">
            <ContentCopyIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {insight.references && insight.references.length > 0 && (
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            References
          </Typography>
          <Stack spacing={1}>
            {insight.references.map((ref, index) => (
              <Box
                key={index}
                sx={{ bgcolor: "action.hover", p: 1, borderRadius: 1 }}
              >
                <Typography variant="caption" color="text.secondary">
                  Page {ref.pageNumber}
                </Typography>
                <Typography variant="body2">{ref.text}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
      <Box mt={1} display="flex" gap={1} alignItems="center">
        <Typography variant="caption" color="text.secondary">
          Created by: {insight.createdBy}
        </Typography>
        {insight.approved !== undefined && (
          <Typography
            variant="caption"
            color={insight.approved ? "success.main" : "error.main"}
          >
            • {insight.approved ? "Approved" : "Disapproved"}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
